import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Phone, ExternalLink, Loader2 } from "lucide-react";
import { SITE } from "../lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GREETING =
  "Bonjour 👋 Je suis l'assistant Termite Habitat 33. Une question sur les termites, le bois, la mérule ou un devis à Bordeaux ?";

function makeSessionId() {
  const k = "th33_chat_session";
  let id = localStorage.getItem(k);
  if (!id) {
    id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(k, id);
  }
  return id;
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = makeSessionId();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || loading) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: t });
      setMessages((m) => [...m, { role: "assistant", text: data.reply || "—" }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "Une erreur est survenue. Vous pouvez nous joindre directement au 07 45 71 15 10 ou demander un devis gratuit en ligne sur /devis.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 left-5 z-[60] bg-[#7A2E39] hover:bg-[#5E222A] text-white w-14 h-14 rounded-full shadow-lg grid place-items-center transition-transform hover:scale-105"
        aria-label="Ouvrir le chat"
        data-testid="chatbot-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 left-5 z-[60] w-[92vw] max-w-sm bg-white border border-[#D5CABD] rounded-sm shadow-2xl flex flex-col overflow-hidden"
          data-testid="chatbot-window"
        >
          <div className="bg-[#2B2118] text-white p-4 flex items-center gap-3">
            <img
              src="/brand/logo.png"
              alt="Termite Habitat 33"
              width="40"
              height="40"
              className="w-10 h-10 rounded-full bg-white p-1 object-contain"
            />
            <div className="flex-1">
              <div className="font-serif text-lg leading-tight">Termite Habitat 33</div>
              <div className="text-[10px] tracking-widest uppercase text-white/60">Assistant en ligne · répond en quelques secondes</div>
            </div>
          </div>

          <div ref={scrollRef} className="p-4 space-y-3 max-h-[60vh] overflow-y-auto bg-[#F9F8F6]">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.role}`}
              >
                <div
                  className={`max-w-[85%] rounded-sm px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#7A2E39] text-white"
                      : "bg-white border border-[#D5CABD] text-gray-800"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> L'expert vous répond…
              </div>
            )}
          </div>

          <div className="border-t border-[#D5CABD] p-2 flex flex-wrap gap-2 bg-white">
            {["Expertise obligatoire ?", "Prix d'un traitement ?", "J'ai vu des termites"].map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                className="text-[11px] bg-[#E8DCC9]/60 hover:bg-[#E8DCC9] border border-[#D5CABD] px-2 py-1 rounded-sm"
                data-testid={`chat-quick-${q.slice(0, 6)}`}
              >
                {q}
              </button>
            ))}
          </div>

          <div className="border-t border-[#D5CABD] p-3 flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Écrivez votre question…"
              className="flex-1 resize-none border border-[#D5CABD] rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#7A2E39]"
              data-testid="chat-input"
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="bg-[#7A2E39] hover:bg-[#5E222A] disabled:opacity-50 text-white px-3 rounded-sm"
              data-testid="chat-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          <div className="border-t border-[#D5CABD] grid grid-cols-2 gap-0 bg-white">
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="flex items-center justify-center gap-1 text-xs py-2 text-gray-700 hover:bg-[#F9F8F6] border-r border-[#D5CABD]"
              data-testid="chat-call"
            >
              <Phone className="w-3.5 h-3.5" /> Appeler
            </a>
            <a
              href="/devis"
              className="flex items-center justify-center gap-1 text-xs py-2 text-gray-700 hover:bg-[#F9F8F6]"
              data-testid="chat-devis"
            >
              Devis gratuit <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </>
  );
}
